"use client";

import { useMemo } from "react";
import { useBookmark } from "@/app/hooks/use-bookmark";
import TeamCard from "@/app/(pages)/(frontend)/t/[tenant]/ansprechpartner/components/team-card";
import type { ExtendedContactPersonT } from "@/app/types/contact-persons";

type BookmarkedContactsSectionPropsT = {
  allContacts: ExtendedContactPersonT[];
};

/**
 * Client component that displays bookmarked contact persons
 * Optimized to only update locally when bookmarks are removed, without refetching
 */
export default function BookmarkedContactsSection({
  allContacts,
}: BookmarkedContactsSectionPropsT) {
  const { contactPersonIds } = useBookmark();

  // Memoize filtered and ordered contact persons based on bookmarked IDs
  const bookmarkedContacts = useMemo(() => {
    if (contactPersonIds.length === 0) {
      return [];
    }

    // Create a Set for O(1) lookup performance
    const contactPersonIdSet = new Set(contactPersonIds);

    // Filter contact persons by bookmarked IDs from cache
    const filtered = allContacts.filter((contact) =>
      contactPersonIdSet.has(contact.id)
    );

    // Maintain order of bookmarks
    return contactPersonIds
      .map((id) => filtered.find((c) => c.id === id))
      .filter((c): c is ExtendedContactPersonT => c !== undefined);
  }, [contactPersonIds, allContacts]);

  if (bookmarkedContacts.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 grid grid-cols-1 gap-6 gap-y-6 lg:grid-cols-2">
      {bookmarkedContacts.map((contact) => (
        <div className="last:mb-6" key={contact.id}>
          <TeamCard
            key={contact.id}
            id={contact.id}
            name={contact.name}
            title={contact.title}
            phone={contact.phone}
            email={contact.email}
            image={contact.image}
            makes={contact.makes}
            locations={contact.locations}
            languages={contact.languages}
            isNameClickable={true}
            slug={contact.slug}
          />
        </div>
      ))}
    </div>
  );
}
